import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Copy,
  Edit,
  Grid3X3,
  Hash,
  LayoutGrid,
  List,
  MessageSquare,
  Plus,
  Save,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Input, TextArea } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';

interface SMSTemplate {
  id: string;
  name: string;
  message: string;
  category: string;
  description?: string;
  variables: string[];
  characterCount: number;
  messageCount: number;
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  usageCount: number;
}

interface TemplateVariable {
  name: string;
  description: string;
  example: string;
  maxLength?: number;
}

const SMSTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<SMSTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<SMSTemplate | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editMode, setEditMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'card' | 'list'>('grid');

  // Editor state
  const [templateData, setTemplateData] = useState({
    name: '',
    message: '',
    category: '',
    description: '',
    variables: [] as string[],
  });

  const categories = [
    { value: 'alerts', label: 'Alerts & Notifications' },
    { value: 'reminders', label: 'Reminders' },
    { value: 'confirmations', label: 'Confirmations' },
    { value: 'invitations', label: 'Invitations' },
    { value: 'updates', label: 'Status Updates' },
    { value: 'verification', label: 'Verification' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'marketing', label: 'Marketing' },
  ];

  const availableVariables: TemplateVariable[] = [
    {
      name: '{{user.firstName}}',
      description: "User's first name",
      example: 'John',
      maxLength: 20,
    },
    { name: '{{user.lastName}}', description: "User's last name", example: 'Doe', maxLength: 20 },
    {
      name: '{{user.phone}}',
      description: "User's phone number",
      example: '+1234567890',
      maxLength: 15,
    },
    {
      name: '{{project.name}}',
      description: 'Project name',
      example: 'Office Building',
      maxLength: 30,
    },
    { name: '{{project.code}}', description: 'Project code', example: 'OB-2024', maxLength: 10 },
    { name: '{{company.name}}', description: 'Company name', example: 'BuilderBel', maxLength: 25 },
    { name: '{{date.current}}', description: 'Current date', example: '2024-01-15', maxLength: 10 },
    { name: '{{time.current}}', description: 'Current time', example: '14:30', maxLength: 5 },
    {
      name: '{{verification.code}}',
      description: 'Verification code',
      example: '123456',
      maxLength: 10,
    },
    { name: '{{meeting.time}}', description: 'Meeting time', example: '2:30 PM', maxLength: 10 },
    {
      name: '{{task.name}}',
      description: 'Task name',
      example: 'Foundation Review',
      maxLength: 25,
    },
    {
      name: '{{url.short}}',
      description: 'Shortened URL',
      example: 'bit.ly/abc123',
      maxLength: 20,
    },
  ];

  const sampleTemplates: SMSTemplate[] = [
    {
      id: '1',
      name: 'Verification Code',
      message:
        'Your {{company.name}} verification code is: {{verification.code}}. This code expires in 10 minutes.',
      category: 'verification',
      description: 'SMS verification code for two-factor authentication',
      variables: ['{{company.name}}', '{{verification.code}}'],
      characterCount: 85,
      messageCount: 1,
      isActive: true,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      usageCount: 234,
    },
    {
      id: '2',
      name: 'Meeting Reminder',
      message:
        'Reminder: You have a meeting for {{project.name}} at {{meeting.time}} today. Reply CONFIRM to acknowledge.',
      category: 'reminders',
      description: 'Meeting reminder for project stakeholders',
      variables: ['{{project.name}}', '{{meeting.time}}'],
      characterCount: 105,
      messageCount: 1,
      isActive: true,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      usageCount: 156,
    },
    {
      id: '3',
      name: 'Project Status Update',
      message:
        'Project Update: {{project.name}} ({{project.code}}) - {{task.name}} has been completed. Check the app for details: {{url.short}}',
      category: 'updates',
      description: 'Notification when project tasks are completed',
      variables: ['{{project.name}}', '{{project.code}}', '{{task.name}}', '{{url.short}}'],
      characterCount: 135,
      messageCount: 1,
      isActive: true,
      isSystem: false,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      usageCount: 89,
    },
    {
      id: '4',
      name: 'Emergency Alert',
      message:
        'URGENT: Safety alert for {{project.name}}. All personnel must evacuate immediately. Report to safety coordinator for instructions.',
      category: 'emergency',
      description: 'Emergency notification for safety incidents',
      variables: ['{{project.name}}'],
      characterCount: 132,
      messageCount: 1,
      isActive: true,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      usageCount: 2,
    },
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    // Update character and message count when message changes
    const { characterCount, messageCount } = calculateSMSMetrics(templateData.message);
    if (templateData.message) {
      setTemplateData(prev => ({ ...prev, characterCount, messageCount }));
    }
  }, [templateData.message]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      // In production, this would fetch from API
      setTemplates(sampleTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      setMessage({ type: 'error', text: 'Failed to load SMS templates' });
    } finally {
      setLoading(false);
    }
  };

  const calculateSMSMetrics = (text: string) => {
    const length = text.length;

    // Standard SMS is 160 characters, but with special characters it can be 70
    // For simplicity, we'll use 160 as the standard limit
    const singleSMSLimit = 160;
    const multiSMSLimit = 153; // Each SMS in a multi-part message is 153 characters

    const characterCount = length;
    let messageCount = 1;

    if (length > singleSMSLimit) {
      messageCount = Math.ceil(length / multiSMSLimit);
    }

    return { characterCount, messageCount };
  };

  const handleCreateTemplate = () => {
    setEditMode('create');
    setTemplateData({
      name: '',
      message: '',
      category: '',
      description: '',
      variables: [],
    });
    setShowEditor(true);
  };

  const handleEditTemplate = (template: SMSTemplate) => {
    setEditMode('edit');
    setSelectedTemplate(template);
    setTemplateData({
      name: template.name,
      message: template.message,
      category: template.category,
      description: template.description || '',
      variables: template.variables,
    });
    setShowEditor(true);
  };

  const handleSaveTemplate = async () => {
    try {
      // Validate template data
      if (!templateData.name || !templateData.message || !templateData.category) {
        setMessage({ type: 'error', text: 'Please fill in all required fields' });
        return;
      }

      // Extract variables from message
      const messageVariables = extractVariables(templateData.message);
      const { characterCount, messageCount } = calculateSMSMetrics(templateData.message);

      const templateToSave = {
        ...templateData,
        variables: messageVariables,
        characterCount,
        messageCount,
        id: editMode === 'create' ? Date.now().toString() : selectedTemplate!.id,
        isActive: true,
        isSystem: false,
        createdAt: editMode === 'create' ? new Date().toISOString() : selectedTemplate!.createdAt,
        updatedAt: new Date().toISOString(),
        usageCount: editMode === 'create' ? 0 : selectedTemplate!.usageCount,
      };

      if (editMode === 'create') {
        setTemplates(prev => [...prev, templateToSave as SMSTemplate]);
      } else {
        setTemplates(prev =>
          prev.map(t => (t.id === templateToSave.id ? (templateToSave as SMSTemplate) : t))
        );
      }

      setShowEditor(false);
      setMessage({
        type: 'success',
        text: `Template ${editMode === 'create' ? 'created' : 'updated'} successfully!`,
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage({ type: 'error', text: 'Failed to save template' });
    }
  };

  const handleDeleteTemplate = async (template: SMSTemplate) => {
    if (template.isSystem) {
      setMessage({ type: 'error', text: 'Cannot delete system templates' });
      return;
    }

    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      setMessage({ type: 'success', text: 'Template deleted successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting template:', error);
      setMessage({ type: 'error', text: 'Failed to delete template' });
    }
  };

  const handleDuplicateTemplate = (template: SMSTemplate) => {
    const duplicated = {
      ...template,
      id: Date.now().toString(),
      name: `${template.name} (Copy)`,
      isSystem: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      usageCount: 0,
    };
    setTemplates(prev => [...prev, duplicated]);
    setMessage({ type: 'success', text: 'Template duplicated successfully' });
    setTimeout(() => setMessage(null), 3000);
  };

  const extractVariables = (content: string): string[] => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const matches = content.match(variableRegex);
    return matches ? [...new Set(matches)] : [];
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('smsMessage') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setTemplateData(prev => ({ ...prev, message: newText }));

      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const getCharacterCountColor = (count: number) => {
    if (count <= 160) return 'text-green-600';
    if (count <= 320) return 'text-red-500';
    return 'text-red-600';
  };

  const getMessageCountBadge = (count: number) => {
    if (count === 1) return 'bg-green-100 text-green-800';
    if (count <= 3) return 'bg-red-100 text-red-700';
    return 'bg-red-100 text-red-800';
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  if (showEditor) {
    const { characterCount, messageCount } = calculateSMSMetrics(templateData.message);

    return (
      <div className="max-w-7xl mx-auto py-6 px-2">
        {/* Editor Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowEditor(false)}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {editMode === 'create' ? 'Create' : 'Edit'} SMS Template
              </h1>
              <p className="text-sm text-gray-600">
                Design and configure SMS templates with variables and character optimization
              </p>
            </div>
          </div>

          <button
            onClick={handleSaveTemplate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            <Save className="h-4 w-4 mr-2" />
            Save Template
          </button>
        </div>

        {/* Status Message */}
        {message && (
          <div
            className={`rounded-lg p-4 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            <div className="flex items-center">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5 mr-2" />
              ) : (
                <AlertCircle className="h-5 w-5 mr-2" />
              )}
              {message.text}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Template Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Template Name"
                  value={templateData.name}
                  onChange={e => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Meeting Reminder"
                  required
                />

                <Dropdown
                  label="Category"
                  options={categories}
                  value={templateData.category}
                  onChange={value => setTemplateData(prev => ({ ...prev, category: value }))}
                  placeholder="Select category"
                  required
                />
              </div>

              <TextArea
                label="Description"
                value={templateData.description}
                onChange={value => setTemplateData(prev => ({ ...prev, description: value }))}
                placeholder="Brief description of when this template is used..."
                rows={3}
              />
            </div>

            {/* Message Editor */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">SMS Message</h3>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Hash className="h-4 w-4 text-gray-400" />
                    <span
                      className={`text-sm font-medium ${getCharacterCountColor(characterCount)}`}
                    >
                      {characterCount} characters
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MessageSquare className="h-4 w-4 text-gray-400" />
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getMessageCountBadge(messageCount)}`}
                    >
                      {messageCount} SMS
                    </span>
                  </div>
                </div>
              </div>

              <textarea
                id="smsMessage"
                value={templateData.message}
                onChange={e => setTemplateData(prev => ({ ...prev, message: e.target.value }))}
                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-red-400 resize-none"
                placeholder="Enter your SMS message here..."
                maxLength={500}
              />

              <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
                <div>
                  <span>Recommended: Keep under 160 characters for single SMS</span>
                </div>
                <div>
                  {characterCount > 160 && (
                    <span className="text-red-500">
                      Multiple SMS required ({messageCount} parts)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* SMS Preview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
              <div className="bg-gray-50 rounded-lg p-4 max-w-sm">
                <div className="bg-brand-500 text-white p-3 rounded-lg text-sm">
                  {templateData.message || 'No message content'}
                </div>
                <div className="mt-2 text-xs text-gray-500 text-right">
                  SMS • {characterCount} chars • {messageCount} part{messageCount !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Available Variables */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Variables</h3>
              <div className="space-y-2">
                {availableVariables.map(variable => (
                  <div key={variable.name} className="group">
                    <button
                      onClick={() => insertVariable(variable.name)}
                      className="w-full text-left p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="font-mono text-sm text-red-500">{variable.name}</div>
                      <div className="text-xs text-gray-600">{variable.description}</div>
                      <div className="text-xs text-gray-400">
                        Example: {variable.example}
                        {variable.maxLength && ` (max ${variable.maxLength} chars)`}
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* SMS Guidelines */}
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-brand-900 mb-4">SMS Best Practices</h3>
              <ul className="space-y-2 text-sm text-brand-800">
                <li>• Keep messages under 160 characters when possible</li>
                <li>• Use clear, concise language</li>
                <li>• Include a clear call-to-action</li>
                <li>• Test with sample data before sending</li>
                <li>• Consider timezone for time-sensitive messages</li>
                <li>• Use variables to personalize messages</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/dashboard/settings')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">SMS Templates</h1>
            <p className="text-sm text-gray-600">
              Create and manage SMS message templates for alerts, reminders, and notifications
            </p>
          </div>
        </div>

        <button
          onClick={handleCreateTemplate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </button>
      </div>

      {/* Status Message */}
      {message && (
        <div
          className={`rounded-lg p-4 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-red-400"
              />
            </div>

            <Dropdown
              label=""
              placeholder="All Categories"
              options={[{ value: '', label: 'All Categories' }, ...categories]}
              value={selectedCategory}
              onChange={setSelectedCategory}
            />
          </div>

          <div className="flex items-center justify-between lg:justify-end gap-4">
            <span className="text-sm text-gray-600">
              {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
            </span>

            {/* View Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-red-500 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Grid View"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'card'
                    ? 'bg-white text-red-500 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Card View"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-red-500 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="List View"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Templates Display */}
      <div className="space-y-6">
        {viewMode === 'grid' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Template
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message Preview
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SMS Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTemplates.map(template => (
                    <tr key={template.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900">{template.name}</div>
                            {template.isSystem && (
                              <span className="px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 rounded-full">
                                System
                              </span>
                            )}
                          </div>
                          {template.description && (
                            <div className="text-sm text-gray-500 mt-1">{template.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {categories.find(c => c.value === template.category)?.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {template.message}
                        </div>
                        <div className="flex items-center space-x-1 mt-1">
                          <Hash className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {template.characterCount} chars
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{template.usageCount}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getMessageCountBadge(template.messageCount)}`}
                        >
                          {template.messageCount} SMS
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            template.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {template.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>

                          <button
                            onClick={() => handleDuplicateTemplate(template)}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Duplicate"
                          >
                            <Copy className="h-4 w-4" />
                          </button>

                          {!template.isSystem && (
                            <button
                              onClick={() => handleDeleteTemplate(template)}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {viewMode === 'card' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                      {template.isSystem && (
                        <span className="px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 rounded-full">
                          System
                        </span>
                      )}
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          template.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {template.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {template.description && (
                      <p className="text-sm text-gray-500 mb-3">{template.description}</p>
                    )}

                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-sm text-gray-700 truncate">{template.message}</p>
                    </div>

                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>
                        Category: {categories.find(c => c.value === template.category)?.label}
                      </span>
                      <span>Used: {template.usageCount} times</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => handleDuplicateTemplate(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </button>

                    {!template.isSystem && (
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <Hash className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500">{template.characterCount}</span>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getMessageCountBadge(template.messageCount)}`}
                    >
                      {template.messageCount} SMS
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {viewMode === 'list' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-200">
            {filteredTemplates.map(template => (
              <div key={template.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="text-sm font-medium text-gray-900">{template.name}</h3>
                          {template.isSystem && (
                            <span className="px-2 py-1 text-xs font-medium bg-brand-100 text-brand-800 rounded-full">
                              System
                            </span>
                          )}
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              template.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {template.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                          <span>{categories.find(c => c.value === template.category)?.label}</span>
                          <span>Used: {template.usageCount} times</span>
                          <span>{template.characterCount} chars</span>
                          <span
                            className={getMessageCountBadge(template.messageCount).replace(
                              'px-2 py-1 text-xs font-medium rounded-full ',
                              ''
                            )}
                          >
                            {template.messageCount} SMS
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => handleDuplicateTemplate(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </button>

                    {!template.isSystem && (
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || selectedCategory
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first SMS template.'}
            </p>
            <button
              onClick={handleCreateTemplate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SMSTemplates;
